import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Server, Boxes } from "lucide-react";

export default function SettingsDialog({ open, onOpenChange, onSaved }) {
  const [status, setStatus] = useState(null);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [port, setPort] = useState("");
  const [savingPort, setSavingPort] = useState(false);
  const [decodoUser, setDecodoUser] = useState("");
  const [decodoPass, setDecodoPass] = useState("");
  const [savingDecodo, setSavingDecodo] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings");
      setStatus(data);
      if (data.gateway_port) setPort(String(data.gateway_port));
    } catch (e) {
      /* ignore */
    }
  };

  const savePort = async () => {
    const p = parseInt(port, 10);
    if (!p) return;
    setSavingPort(true);
    try {
      const { data } = await api.post("/settings", { gateway_port: p });
      toast.success(`Gateway moved to port ${data.gateway_port}`);
      await load();
      onSaved && onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSavingPort(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const save = async () => {
    if (!value.trim()) return;
    setSaving(true);
    try {
      await api.post("/settings", { api_key: value.trim() });
      toast.success("API key saved");
      setValue("");
      await load();
      onSaved && onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const saveDecodo = async () => {
    if (!decodoUser.trim() || !decodoPass.trim()) return;
    setSavingDecodo(true);
    try {
      await api.post("/settings", { decodo_username: decodoUser.trim(), decodo_password: decodoPass.trim() });
      toast.success("Decodo credentials saved");
      setDecodoUser("");
      setDecodoPass("");
      await load();
      onSaved && onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSavingDecodo(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-panel border-line text-foreground max-w-md" data-testid="settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 tracking-tight">
            <KeyRound size={18} className="text-neon" /> Settings
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-sm text-dim">
            The key is used in the <span className="font-mono text-neon">X-Api-Key</span> header to fetch the proxy list from FloppyData.
          </div>
          <div className="card-panel p-3 flex items-center justify-between">
            <span className="overline">Status</span>
            {status?.configured ? (
              <span className="font-mono text-sm text-laser" data-testid="settings-status">
                {status.api_key_masked} · {status.source}
              </span>
            ) : (
              <span className="font-mono text-sm text-signal">not configured</span>
            )}
          </div>
          <Input
            data-testid="settings-apikey-input"
            placeholder="Paste a new API key..."
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="font-mono bg-black border-line focus-visible:ring-neon"
          />
          <div className="flex justify-end gap-2">
            <Button
              onClick={save}
              disabled={saving || !value.trim()}
              className="bg-neon text-white hover:bg-neon/85 rounded-none font-medium"
              data-testid="settings-save-btn"
            >
              {saving ? "Saving..." : "Save Key"}
            </Button>
          </div>

          <div className="border-t border-line pt-4 space-y-2">
            <div className="flex items-center gap-2">
              <Server size={15} className="text-laser" />
              <span className="overline">Proxy Server Port (gateway)</span>
            </div>
            <p className="text-xs text-dim">
              The port your browser/OS uses as a proxy (<span className="font-mono text-foreground">server-address:{status?.gateway_port || "…"}</span>). Change it if the port conflicts.
            </p>
            <div className="flex gap-2">
              <Input
                type="number"
                min={1024}
                max={65535}
                value={port}
                onChange={(e) => setPort(e.target.value)}
                className="font-mono bg-black border-line focus-visible:ring-neon w-40"
                data-testid="settings-port-input"
              />
              <Button
                onClick={savePort}
                disabled={savingPort || !port || String(status?.gateway_port) === port}
                variant="outline"
                className="rounded-none border-line hover:border-laser hover:text-laser font-mono"
                data-testid="settings-port-save-btn"
              >
                {savingPort ? "Changing..." : "Change Port"}
              </Button>
            </div>
          </div>

          <div className="border-t border-line pt-4 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Boxes size={15} className="text-neon" />
                <span className="overline">Decodo Provider (SOCKS5)</span>
              </div>
              {status?.decodo_configured ? (
                <span className="font-mono text-xs text-laser" data-testid="decodo-settings-status">
                  {status.decodo_username || "configured"}
                </span>
              ) : (
                <span className="font-mono text-xs text-signal">not configured</span>
              )}
            </div>
            <p className="text-xs text-dim">
              Proxy username &amp; password from your Decodo dashboard (Proxy setup). Used to generate SOCKS5 pool entries via Decodo gateways.
            </p>
            <Input
              placeholder="Decodo username"
              value={decodoUser}
              onChange={(e) => setDecodoUser(e.target.value)}
              className="font-mono bg-black border-line focus-visible:ring-neon"
              data-testid="settings-decodo-user"
            />
            <div className="flex gap-2">
              <Input
                type="password"
                placeholder="Decodo password"
                value={decodoPass}
                onChange={(e) => setDecodoPass(e.target.value)}
                className="font-mono bg-black border-line focus-visible:ring-neon"
                data-testid="settings-decodo-pass"
              />
              <Button
                onClick={saveDecodo}
                disabled={savingDecodo || !decodoUser.trim() || !decodoPass.trim()}
                className="bg-neon text-white hover:bg-neon/85 rounded-none font-medium shrink-0"
                data-testid="settings-decodo-save-btn"
              >
                {savingDecodo ? "Saving..." : "Save Decodo"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SettingsTrigger({ children }) {
  return <DialogTrigger asChild>{children}</DialogTrigger>;
}
